import {
  AssignmentWizardData, Contact, WizardContact,
} from '../../../../../providers/api/dtos';
import { AssignmentWizardContactFormModel } from './AssignmentWizardContactForm';
import { WizardCompanyFormModel } from '../../../../../company/components/wizard-company-form/WizardCompanyForm';
import { legalToWizardCompanyFormModel, wizardCompanyFormModelToLegal } from '../../../../../company/components/wizard-company-form/mappers';
import { formatInputDate } from '../../../../../utils/form';
import { geoFormModelToGeo, geoToGeoFormModel } from '../../../../../geo/components/form-geo-field/mappers';
import { IdentityDocumentType } from '../../../../../domain/types';

export function mapToWizardContact(contact: Contact): WizardContact;
export function mapToWizardContact(contact?: Contact): WizardContact | undefined;

export function mapToWizardContact(contact?: Contact): WizardContact | undefined {
  if (typeof contact !== 'undefined') {
    return {
      id: contact?.id || undefined,
      name: contact?.name,
      email: contact?.email,
      phoneNumber: contact?.phoneNumber,
      birthday: contact?.birthday,
      birthplace: contact?.birthplace,
      fiscalCode: contact?.fiscalCode,
      placeOfResidence: contact.residence,
    };
  }

  return undefined;
}

export const toAssignmentWizardSellerModel = (initialData: AssignmentWizardData, prospectContact?: Contact) => {
  const contacts: WizardContact[] = initialData.contacts || [mapToWizardContact(prospectContact)].filter((item) => item) as WizardContact[];

  const firstLegalContact = contacts?.find((contact) => contact.legal !== undefined)?.legal;

  return legalToWizardCompanyFormModel(firstLegalContact);
};

export const toAssignmentWizardData = async (existingData: AssignmentWizardData, values: WizardCompanyFormModel, updatedContacts: Contact[], isLegalPersonOnly?: boolean) => {
  const hasPersonTypeChanged = !!existingData.isLegalPersonOnly !== !!isLegalPersonOnly;

  const newContacts: WizardContact[] = updatedContacts.map((contact) => {
    const existingContact = existingData.contacts?.find(({ id }) => contact.id === id);

    return {
      ...existingContact,
      legal: undefined,
      id: contact.id,
      ...(hasPersonTypeChanged && { antiMoneyLaundering: undefined }),
      overriddenFields: {
        ...existingContact?.overriddenFields,
        name: contact.name,
        phoneNumber: contact.phoneNumber,
        email: contact.email,
        fiscalCode: contact.fiscalCode,
        birthday: contact.birthday,
        birthplace: contact.birthplace,
        residence: contact.residence,
        notes: contact.notes,
        citizenship: contact.citizenship,
        identityDocument: contact.identityDocument,
      },
      updatedAt: contact.updatedAt,
    };
  });

  if (isLegalPersonOnly && updatedContacts.length > 0) {
    const legalContact = newContacts[0];

    newContacts[0] = { ...legalContact, legal: wizardCompanyFormModelToLegal(values) };
  }

  const newValues: AssignmentWizardData = {
    ...existingData,
    isLegalPersonOnly,
    contacts: newContacts,
    isDigitallySigned: false,
  };

  return newValues;
};

export const toPropertyContact = (contact: WizardContact): AssignmentWizardContactFormModel => {
  const { identityDocument } = contact;

  return {
    name: contact.name || '',
    phoneNumber: contact.phoneNumber || '',
    email: contact.email || '',
    birthday: contact.birthday ? formatInputDate(new Date(contact.birthday)) : '',
    birthplace: contact.birthplace || '',
    fiscalCode: contact.fiscalCode || '',
    citizenship: contact.citizenship || '',
    placeOfResidence: geoToGeoFormModel(contact.placeOfResidence),
    identityDocument: {
      number: identityDocument?.number || '',
      issuedAt: identityDocument?.issuedAt ? formatInputDate(new Date(identityDocument?.issuedAt)) : '',
      issuedBy: identityDocument?.issuedBy || '',
      type: identityDocument?.type ? IdentityDocumentType[identityDocument.type] : '',
    },
  };
};

export const toWizardContact = (
  contact: WizardContact,
  values: AssignmentWizardContactFormModel,
): WizardContact => {
  const {
    identityDocument,
  } = values;

  return ({
    ...contact,
    name: values.name.trim() || undefined,
    phoneNumber: values.phoneNumber || undefined,
    email: values.email.trim() || undefined,
    birthday: values.birthday ? new Date(values.birthday).toISOString() : undefined,
    birthplace: values.birthplace || undefined,
    fiscalCode: values.fiscalCode || undefined,
    citizenship: values.citizenship,
    identityDocument: {
      type: identityDocument ? IdentityDocumentType[identityDocument.type as IdentityDocumentType] : undefined,
      number: identityDocument?.number,
      issuedAt: identityDocument?.issuedAt,
      issuedBy: identityDocument?.issuedBy,
    },
    placeOfResidence: geoFormModelToGeo(values.placeOfResidence),
  });
};
